/**
 * Product detail hero (converted from the Webflow detail_product template grid).
 *
 * Owns the interactive state shared across the two grid cells: the selected
 * option values → matching variant → displayed price, the gallery's active
 * image, the quantity, and add-to-cart. Selecting an option resolves the
 * matching variant; if that variant has its own image the gallery swaps to it.
 *
 * Grid placement ids (`w-node-…`) are preserved from the export so the Webflow
 * grid layout is reproduced exactly. The page-load reveal classes
 * (`animation-on-load-01/03/04`) are driven globally by SiteAnimations.
 */
import type { ChangeEvent, FC, FormEvent } from 'react'
import type { Product, ProductVariant } from '@/types'

import { useMemo, useState } from 'react'

import { useCart } from '@/lib/cart'
import { formatPrice } from '@/lib/format'
import { ProductGallery } from '@/components/product/product-gallery'

interface ProductDetailProps {
    product: Product
}

/** Find the variant whose option values match the current selection. */
const matchVariant = (
    product: Product,
    selection: Record<string, string>
): ProductVariant | undefined => {
    return product.variants.find((variant) => {
        const pairs: Array<[string, string]> = [
            [variant.option1Name, variant.option1Value],
            [variant.option2Name, variant.option2Value]
        ]

        return pairs.every(([name, value]) => {
            if (!name) return true
            return selection[name] === value
        })
    })
}

export const ProductDetail: FC<ProductDetailProps> = ({ product }) => {
    const cart = useCart()

    const initialSelection = useMemo<Record<string, string>>(() => {
        const base = product.variants[0]
        const selection: Record<string, string> = {}
        if (base?.option1Name) selection[base.option1Name] = base.option1Value
        if (base?.option2Name) selection[base.option2Name] = base.option2Value
        return selection
    }, [product])

    const [selection, setSelection] = useState<Record<string, string>>(initialSelection)
    const [quantity, setQuantity] = useState(1)
    const [activeImage, setActiveImage] = useState(0)

    const selectedVariant = useMemo(
        () => matchVariant(product, selection) ?? product.variants[0],
        [product, selection]
    )

    const price = selectedVariant?.price ?? product.price

    const onSelectOption = (name: string, value: string) => {
        const nextSelection = { ...selection, [name]: value }
        setSelection(nextSelection)

        // If the resolved variant has its own image, swap the gallery to it.
        const variant = matchVariant(product, nextSelection)
        if (variant?.image) {
            const index = product.images.indexOf(variant.image)
            if (index >= 0) setActiveImage(index)
        }
    }

    const onAdd = (event: FormEvent) => {
        event.preventDefault()
        const variant = selectedVariant
        if (!variant) return

        cart.addItem({
            productHandle: product.handle,
            variantId: variant.id,
            name: product.name,
            image: variant.image || product.images[activeImage] || product.mainImage,
            price: variant.price || product.price,
            option1: variant.option1Value || '',
            option2: variant.option2Value || '',
            quantity
        })
        cart.openCart()
    }

    return (
        <div className='section'>
            <div className='container'>
                <div className='product-hero-container'>
                    <div className='w-layout-grid product-template-grid'>
                        <div id='w-node-_04ac8371-6878-f2eb-56f7-0f4c4310bcd5-a35bd841' className='animation-on-load-01'>
                            <ProductGallery
                                images={product.images}
                                name={product.name}
                                activeIndex={activeImage}
                                onSelect={setActiveImage}
                            />
                        </div>

                        <div id='w-node-_4e470954-1265-27a0-e34f-5482bdb57cba-a35bd841' className='animation-on-load-01'>
                            <div id='w-node-da1c3f66-0694-d904-c0d3-15fff4d46e96-a35bd841' className='product-content-container'>
                                <div className='animation-on-load-03'>
                                    <div className='product-category-wrapper w-dyn-list'>
                                        <div role='list' className='product-category-list w-dyn-items'>
                                            {product.categories.map((category, index) => (
                                                <div key={category} role='listitem' className='product-category-item w-dyn-item'>
                                                    <div className='category-text-gold'>{category}</div>
                                                    {index < product.categories.length - 1 && (
                                                        <div className='category-text-gold minus'>-</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <p className='no-margins'>{product.name}</p>
                                    <h4 className='price-text-normal'>{formatPrice(price)}</h4>
                                    <p className='paragraph-s grey-text'>{product.description}</p>
                                </div>

                                <div className='animation-on-load-04'>
                                    <div className='add-to-cart'>
                                        <form className='w-commerce-commerceaddtocartform' onSubmit={onAdd}>
                                            {product.options.map((option) => (
                                                <div key={option.name} className='option'>
                                                    <label className='category-text-gold'>{option.name}</label>
                                                    <div className='button-group'>
                                                        {option.values.map((value) => {
                                                            const selected = selection[option.name] === value

                                                            return (
                                                                <button
                                                                    key={value}
                                                                    type='button'
                                                                    className={
                                                                        selected
                                                                            ? 'size-button w--ecommerce-pill-selected'
                                                                            : 'size-button'
                                                                    }
                                                                    aria-pressed={selected}
                                                                    onClick={() => onSelectOption(option.name, value)}
                                                                >
                                                                    {value}
                                                                </button>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            ))}

                                            <label htmlFor='product-quantity' className='category-text-gold'>Quantity</label>
                                            <input
                                                type='number'
                                                inputMode='numeric'
                                                id='product-quantity'
                                                name='commerce-add-to-cart-quantity-input'
                                                min={1}
                                                className='w-commerce-commerceaddtocartquantityinput quantity'
                                                value={quantity}
                                                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                                    setQuantity(Math.max(1, parseInt(event.target.value, 10) || 1))
                                                }
                                            />

                                            <div className='add-to-card-button-holder'>
                                                <input
                                                    type='submit'
                                                    className='w-commerce-commerceaddtocartbutton button add-to-card-button'
                                                    value='ADD TO CART'
                                                />
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
